import {ChangeEvent, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {SecondaryButton} from "../../../../../components/general/SecondaryButton.tsx";
import {PrimaryButton} from "../../../../../components/general/PrimaryButton.tsx";
import {FilterPendingBadge} from "../../../../../components/general/FilterPendingBadge.tsx";
import {TransactionFilterBarProps, TransactionFilters} from "./partnerTransactions.ts";
import {fieldVariants, fieldLabelClasses} from "../../../../../components/general/fieldStyles.ts";
import {cn} from "../../../../../lib/utils.ts";
import Dropdown from "../../../../../components/general/Dropdown.tsx";
import { GLASelector } from "../../../../../components/specific/GLASelector";
import {MagnifyingGlassIcon} from "@heroicons/react/20/solid";
import {formatDateForInput, parseDate} from "../../../../../components/general/DateRangeInput/utils/dateRangeUtils.ts";
import {getInitialDateRange, useFilterStore} from "../../../../../contexts/FilterStore.tsx";
import { DateRangeInput } from "../../../../../components/general/DateRangeInput/DateRangeInput.tsx";
import {
    TransactionAmountComparatorEnum,
    TransactionFlowFilterEnum,
    TransactionMatchFilterEnum,
} from "../../../../../types/enums.ts";

const MATCH_STATUS_OPTIONS = [
    {value: TransactionMatchFilterEnum.ANY, label: "Alle"},
    {value: TransactionMatchFilterEnum.MATCHED, label: "Gematcht"},
    {value: TransactionMatchFilterEnum.UNMATCHED, label: "Ongematcht"},
    {value: TransactionMatchFilterEnum.BOOKED_AWAY, label: "Weggeboekt"},
];
const FLOW_OPTIONS = [
    {value: TransactionFlowFilterEnum.ANY, label: "Beide"},
    {value: TransactionFlowFilterEnum.INFLOW, label: "Inkomend"},
    {value: TransactionFlowFilterEnum.OUTFLOW, label: "Uitgaand"},
];
const AMOUNT_COMPARATOR_OPTIONS = [
    {value: TransactionAmountComparatorEnum.GT, label: "Meer dan"},
    {value: TransactionAmountComparatorEnum.LT, label: "Minder dan"},
];

export default function TransactionFilterBar({
                                                 filters,
                                                 filterOptions,
                                             }: TransactionFilterBarProps) {

    const [searchParams, setSearchParams] = useSearchParams();
    const { state: filterStore, dispatch } = useFilterStore();

    const commitFiltersToUrl = (next: TransactionFilters) => {
        const newSearchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(next)) {
            if (value) {
                newSearchParams.set(key, String(value));
            }
        }
        for (const key of ['sort', 'direction']) {
            const current = searchParams.get(key);
            if (current) {
                newSearchParams.set(key, current);
            }
        }
        setSearchParams(newSearchParams);
    };

    const initialDateRange = useMemo(() => ({
        from: parseDate(filters.dateFrom || filterStore.common.dateRange.from),
        to: parseDate(filters.dateTo || filterStore.common.dateRange.to)
    }), [filters.dateFrom, filters.dateTo, filterStore.common.dateRange.from, filterStore.common.dateRange.to]);

    const [dateRange, setDateRange] = useState(initialDateRange);
    const [flow, setFlow] = useState<TransactionFlowFilterEnum>(
        filters.flow ?? filterStore.transactions.flow ?? TransactionFlowFilterEnum.ANY
    );
    const [amountComparator, setAmountComparator] = useState<TransactionAmountComparatorEnum>(
        filters.amountComparator ?? filterStore.transactions.amountComparator ?? TransactionAmountComparatorEnum.GT
    );
    const [amount, setAmount] = useState<number | ''>(filters.amount || filterStore.transactions.amount || '');
    const [bankAccountId, setBankAccountId] = useState<string>(filters.bankAccountId || filterStore.transactions.bankAccountId || '');
    const [relationIban, setRelationIban] = useState<string>(filters.relationIban || filterStore.transactions.relationIban || '');
    const [generalLedgerAccountId, setGeneralLedgerAccountId] = useState<string>(filters.generalLedgerAccountId || filterStore.transactions.generalLedgerAccountId || '');
    const [matchStatus, setMatchStatus] = useState<TransactionMatchFilterEnum>(
        filters.matchStatus ?? filterStore.transactions.matchStatus ?? TransactionMatchFilterEnum.ANY
    );
    const clientId = filters.clientId || filterStore.common.customerId || '';
    const [search, setSearch] = useState<string>(filters.search || filterStore.transactions.search || '');
    const [bankAccounts, setBankAccounts] = useState<{
        id: string,
        name: string,
        businessId: string
    }[]>(filterOptions.bankAccounts);
    const [isDirty, setIsDirty] = useState(false);


    useEffect(() => {
        setDateRange({
            from: parseDate(filters.dateFrom || filterStore.common.dateRange.from),
            to: parseDate(filters.dateTo || filterStore.common.dateRange.to)
        });
        setFlow(filters.flow ?? filterStore.transactions.flow ?? TransactionFlowFilterEnum.ANY);
        setAmountComparator(
            filters.amountComparator ?? filterStore.transactions.amountComparator ?? TransactionAmountComparatorEnum.GT
        );
        setAmount(filters.amount || filterStore.transactions.amount || '');
        setBankAccountId(filters.bankAccountId || filterStore.transactions.bankAccountId || '');
        setRelationIban(filters.relationIban || filterStore.transactions.relationIban || '');
        setGeneralLedgerAccountId(filters.generalLedgerAccountId || filterStore.transactions.generalLedgerAccountId || '');
        setMatchStatus(filters.matchStatus ?? filterStore.transactions.matchStatus ?? TransactionMatchFilterEnum.ANY);
        setSearch(filters.search || filterStore.transactions.search || '');
        filterBankAccounts(filters.clientId || filterStore.common.customerId || "");
        setIsDirty(false);
    }, [filters, filterStore.common.customerId, filterStore.common.dateRange, filterStore.transactions]);


    const handleSubmit = () => {
        setIsDirty(false);
        const newFilters: TransactionFilters = {};

        const fromStr = formatDateForInput(dateRange.from);
        const toStr = formatDateForInput(dateRange.to);

        if (fromStr) newFilters.dateFrom = fromStr;
        if (toStr) newFilters.dateTo = toStr;
        if (flow !== TransactionFlowFilterEnum.ANY) newFilters.flow = flow;
        if (amount !== '' && typeof amount === 'number' && !isNaN(amount)) {
            newFilters.amount = amount;
            newFilters.amountComparator = amountComparator;
        }
        if (bankAccountId) newFilters.bankAccountId = bankAccountId;
        if (relationIban) newFilters.relationIban = relationIban;
        if (generalLedgerAccountId) newFilters.generalLedgerAccountId = generalLedgerAccountId;
        if (matchStatus !== TransactionMatchFilterEnum.ANY) newFilters.matchStatus = matchStatus;
        if (clientId) newFilters.clientId = clientId;
        if (search) newFilters.search = search;

        
        dispatch({
            type: 'SET_FILTERS',
            payload: {
                common: {
                    customerId: clientId || null,
                    dateRange: (fromStr && toStr) ? { from: fromStr, to: toStr } : filterStore.common.dateRange
                },
                transactions: {
                    flow,
                    amountComparator,
                    amount: (amount !== '' && typeof amount === 'number' && !isNaN(amount)) ? amount : null,
                    bankAccountId: bankAccountId || null,
                    relationIban: relationIban || null,
                    generalLedgerAccountId: generalLedgerAccountId || null,
                    matchStatus,
                    search
                }
            }
        });

        commitFiltersToUrl(newFilters);
    };

    const handleClear = () => {
        dispatch({ type: 'RESET' });
        dispatch({ type: 'SET_FILTERS', payload: { common: { customerId: clientId || null } } });
        setIsDirty(false);
        const range = getInitialDateRange();
        commitFiltersToUrl({
            ...(clientId ? { clientId } : {}),
            dateFrom: range.from,
            dateTo: range.to,
            flow: TransactionFlowFilterEnum.ANY,
            matchStatus: TransactionMatchFilterEnum.ANY,
            search: '',
        });
    };

    const handleAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
        setIsDirty(true);
        const value = e.target.value;
        if (value === '') {
            setAmount('');
        } else {
            const parsed = parseFloat(value);
            setAmount(isNaN(parsed) ? '' : parsed);
        }
    };

    const filterBankAccounts = (clientId: string) => {
        const filtered = clientId !== ""
            ? filterOptions.bankAccounts.filter(ba => ba.businessId === clientId)
            : filterOptions.bankAccounts;

        setBankAccounts(filtered);

        if (bankAccountId && !filtered.some(ba => ba.id === bankAccountId)) {
            setBankAccountId('');
        }
    };

    return (
        <div className="mb-4 bg-white justify-between w-full flex items-end">
            <div className="flex items-end flex-wrap gap-4">
                <div>
                    <label className={cn(fieldLabelClasses, "mb-2")}>
                        Zoeken
                    </label>
                    <div className="relative w-80">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                        </div>
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setIsDirty(true); }}
                            className={cn(fieldVariants(), "pl-10")}
                            placeholder="Omschrijving, relatie, bedrag..."
                        />
                    </div>
                </div>
                <div className="w-[234px]">
                    <label className={cn(fieldLabelClasses, "mb-2")}>
                        Periode
                    </label>
                    <DateRangeInput
                        value={dateRange}
                        onChange={(value) => { setDateRange(value); setIsDirty(true); }}
                    />
                </div>

                <div className="w-40">
                    <label className={cn(fieldLabelClasses, "mb-2")}>
                        Match status
                    </label>
                    <Dropdown
                        selected={MATCH_STATUS_OPTIONS.find(o => o.value === matchStatus) ?? MATCH_STATUS_OPTIONS[0]}
                        options={MATCH_STATUS_OPTIONS}
                        onUpdateFunc={(value) => { setMatchStatus(value as TransactionMatchFilterEnum); setIsDirty(true); }}
                    />
                </div>

                <div className="w-56">
                    <label className={cn(fieldLabelClasses, "mb-2")}>
                        Bankrekening
                    </label>
                    <Dropdown
                        selected={
                            bankAccountId
                                ? {value: bankAccountId, label: bankAccounts.find(a => a.id === bankAccountId)?.name ?? ""}
                                : {value: "", label: "Alle rekeningen"}
                        }
                        options={[{value: "", label: "Alle rekeningen"}, ...bankAccounts.map(a => ({value: a.id, label: a.name}))]}
                        onUpdateFunc={(value) => { setBankAccountId(String(value)); setIsDirty(true); }}
                    />
                </div>


                <div className="w-40">
                    <label className={cn(fieldLabelClasses, "mb-2")}>
                        Flow
                    </label>
                    <Dropdown
                        selected={FLOW_OPTIONS.find(o => o.value === flow) ?? FLOW_OPTIONS[0]}
                        options={FLOW_OPTIONS}
                        onUpdateFunc={(value) => { setFlow(value as TransactionFlowFilterEnum); setIsDirty(true); }}
                    />
                </div>

                <div>
                    <label className={cn(fieldLabelClasses, "mb-2")}>
                        Bedrag
                    </label>
                    <div className="flex gap-2">
                        <div className="w-32 shrink-0">
                            <Dropdown
                                selected={AMOUNT_COMPARATOR_OPTIONS.find(o => o.value === amountComparator) ?? AMOUNT_COMPARATOR_OPTIONS[0]}
                                options={AMOUNT_COMPARATOR_OPTIONS}
                                onUpdateFunc={(value) => { setAmountComparator(value as TransactionAmountComparatorEnum); setIsDirty(true); }}
                            />
                        </div>
                        <input
                            type="number"
                            value={amount}
                            onChange={handleAmountChange}
                            className={cn(fieldVariants(), "flex-1")}
                            placeholder="Bedrag"
                        />
                    </div>
                </div>


                <div>
                    <label className={cn(fieldLabelClasses, "mb-2")}>
                        Relatie IBAN
                    </label>
                    <input
                        type="text"
                        value={relationIban}
                        onChange={(e) => { setRelationIban(e.target.value); setIsDirty(true); }}
                        className={cn(fieldVariants(), "flex-1")}
                        placeholder="Relatie IBAN"
                    />
                </div>

                <div className="w-64">
                    <label className={cn(fieldLabelClasses, "mb-2")}>
                        Grootboekrekening
                    </label>
                    <GLASelector
                        accounts={filterOptions.glAccounts ?? []}
                        value={filterOptions.glAccounts?.find((item) => item.id === generalLedgerAccountId) ?? null}
                        onChange={(account) => { setGeneralLedgerAccountId(account?.id ?? ""); setIsDirty(true); }}
                        placeholder="Alle grootboekrekeningen"
                        size="sm"
                    />
                </div>
            </div>
            <div className="flex items-center justify-end gap-3">
                <FilterPendingBadge show={isDirty}/>
                <SecondaryButton title={"Wissen"} onClick={handleClear}/>
                <PrimaryButton title={"Toepassen"} onClick={handleSubmit}/>
            </div>

        </div>
    );
}